// Local Transfer account block (§6.2): bank + branch pickers, direct IFSC
// entry, NOVA instant marker, account number pair and the automatic
// account-name lookup (reset on any change, stale replies ignored).
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { lookupAccountName } from '@/api/beneficiaries';
import { findBranchByIfsc, getBanks, getBranches } from '@/api/reference';
import { ApiError } from '@/api/client';
import { isValidIfsc, isValidLocalAccountNumber } from '@/utils/phase2Validation';
import { isNovaIfsc } from '@/utils/payments';
import type { BankRef, BranchRef } from '@/types/phase2.types';
import PickerModal from './PickerModal';
import type { BlockErrors, LocalBlock } from './formModel';

interface LocalTransferFieldsProps {
  block: LocalBlock;
  errors: BlockErrors;
  showErrors: boolean;
  onChange: (block: LocalBlock) => void;
}

const LocalTransferFields = ({ block, errors, showErrors, onChange }: LocalTransferFieldsProps) => {
  const [banks, setBanks] = useState<BankRef[]>([]);
  const [branches, setBranches] = useState<BranchRef[]>([]);
  const [bankPickerOpen, setBankPickerOpen] = useState(false);
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);
  const [ifscBankError, setIfscBankError] = useState('');
  const lookupSeq = useRef(0);

  useEffect(() => {
    getBanks().then(setBanks).catch(() => setBanks([]));
  }, []);

  useEffect(() => {
    if (block.bankCode) {
      getBranches(block.bankCode).then(setBranches).catch(() => setBranches([]));
    } else {
      setBranches([]);
    }
  }, [block.bankCode]);

  const set = (patch: Partial<LocalBlock>) => onChange({ ...block, ...patch });

  // Account-name lookup (§6.2.8): runs by itself once the IFSC is valid and
  // both numbers match. Any change to bank/IFSC/numbers resets it; replies
  // that arrive after a newer lookup started are ignored.
  const numbersMatch =
    block.accountNumber &&
    block.accountNumber2 &&
    block.accountNumber === block.accountNumber2 &&
    isValidLocalAccountNumber(block.accountNumber);
  const lookupReady = Boolean(block.bankCode && isValidIfsc(block.ifsc) && numbersMatch);

  useEffect(() => {
    if (!lookupReady) {
      if (block.lookup !== 'IDLE' || block.accountName) {
        set({ lookup: 'IDLE', accountName: '' });
      }
      return;
    }
    const seq = ++lookupSeq.current;
    set({ lookup: 'CHECKING', accountName: '' });
    const timer = setTimeout(() => {
      lookupAccountName(block.ifsc, block.accountNumber)
        .then((result) => {
          if (lookupSeq.current !== seq) return; // a newer lookup started
          if (result.outcome === 'FOUND') {
            set({ lookup: 'FOUND', accountName: result.holderName ?? '' });
          } else {
            set({ lookup: 'NOT_FOUND', accountName: '' });
          }
        })
        .catch((err) => {
          if (lookupSeq.current !== seq) return;
          set({ lookup: 'IDLE', accountName: '' });
          toast.error(err instanceof ApiError ? err.message : 'Could not check the account. Try again.');
        });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupReady, block.ifsc, block.accountNumber, block.accountNumber2, block.bankCode]);

  const pickBank = (bank: BankRef) => {
    // Picking a bank clears the IFSC, branch and account name (§6.2.1).
    set({
      bankCode: bank.code,
      bankName: bank.name,
      ifscPrefix: bank.ifscPrefix,
      branchName: '',
      branchCity: '',
      ifsc: '',
      accountName: '',
      lookup: 'IDLE',
    });
    setIfscBankError('');
    setBankPickerOpen(false);
  };

  const pickBranch = (branch: BranchRef) => {
    set({ branchName: branch.name, branchCity: branch.city, ifsc: branch.ifsc });
    setIfscBankError('');
    setBranchPickerOpen(false);
  };

  const handleIfscTyped = async (raw: string) => {
    const ifsc = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
    set({ ifsc, branchName: '', branchCity: '' });
    setIfscBankError('');
    if (!isValidIfsc(ifsc)) return;
    // Another bank's prefix (§6.2.4): compare against the chosen bank.
    if (block.ifscPrefix && !ifsc.startsWith(block.ifscPrefix)) {
      const owner = banks.find((b) => ifsc.startsWith(b.ifscPrefix));
      if (owner) {
        setIfscBankError(`This IFSC belongs to ${owner.name}. Pick that bank or check the code.`);
        return;
      }
    }
    // A typed IFSC that matches a branch selects it.
    try {
      const match = await findBranchByIfsc(ifsc);
      if (match) {
        if (!block.bankCode) {
          set({
            bankCode: match.bank.code,
            bankName: match.bank.name,
            ifscPrefix: match.bank.ifscPrefix,
            branchName: match.branch.name,
            branchCity: match.branch.city,
            ifsc: match.branch.ifsc,
          });
        } else if (match.bank.code === block.bankCode) {
          set({ branchName: match.branch.name, branchCity: match.branch.city, ifsc: match.branch.ifsc });
        } else {
          setIfscBankError(`This IFSC belongs to ${match.bank.name}. Pick that bank or check the code.`);
        }
      }
    } catch {
      // lookup failures are non-fatal; the account check will surface errors
    }
  };

  const branchLabel = block.branchName
    ? `${block.branchName}, ${block.branchCity} · ${block.ifsc}`
    : '';

  return (
    <div className="nova-block-fields">
      <div className="mb-3">
        <label className="form-label">Bank name</label>
        <div className="nova-field-row">
          <button
            type="button"
            className={`form-control text-start nova-picker-trigger ${showErrors && !block.bankCode ? 'is-invalid' : ''}`}
            onClick={() => setBankPickerOpen(true)}
          >
            {block.bankName || 'Select bank name'}
          </button>
          <input
            type="text"
            className="form-control nova-bank-code"
            value={block.ifscPrefix}
            disabled
            aria-label="Bank code"
            placeholder="Code"
          />
        </div>
      </div>

      {!block.bankCode && <div className="nova-info-box">Select a bank to view branches.</div>}

      {block.bankCode && (
        <div className="mb-3">
          <label className="form-label">Branch</label>
          <button
            type="button"
            className="form-control text-start nova-picker-trigger"
            onClick={() => setBranchPickerOpen(true)}
          >
            {branchLabel || 'Select branch'}
          </button>
        </div>
      )}

      <div className="mb-3">
        <button
          type="button"
          className="nova-link-btn"
          onClick={() => set({ showIfscDirect: !block.showIfscDirect })}
        >
          Know the IFSC? Enter it directly
        </button>
        {(block.showIfscDirect || block.ifsc) && (
          <>
            <input
              type="text"
              className={`form-control mt-2 ${(showErrors && errors.ifsc) || ifscBankError ? 'is-invalid' : ''}`}
              placeholder="HDFC0001234"
              value={block.ifsc}
              maxLength={11}
              onChange={(e) => void handleIfscTyped(e.target.value)}
            />
            {(ifscBankError || (showErrors && errors.ifsc)) && (
              <div className="invalid-feedback d-block">{ifscBankError || errors.ifsc}</div>
            )}
          </>
        )}
      </div>

      {isNovaIfsc(block.ifsc) && (
        <div className="nova-nova-instant">
          <FontAwesomeIcon icon={faBolt} /> NovaBank account · instant transfer
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">Account number</label>
        <input
          type="text"
          inputMode="numeric"
          className={`form-control ${showErrors && errors.accountNumber ? 'is-invalid' : ''}`}
          value={block.accountNumber}
          maxLength={18}
          onChange={(e) => set({ accountNumber: e.target.value.replace(/\D/g, '').slice(0, 18) })}
        />
        {showErrors && errors.accountNumber && (
          <div className="invalid-feedback d-block">{errors.accountNumber}</div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label">Re-enter account number</label>
        <input
          type="password"
          inputMode="numeric"
          className={`form-control ${showErrors && errors.accountNumber2 ? 'is-invalid' : ''}`}
          value={block.accountNumber2}
          maxLength={18}
          onChange={(e) => set({ accountNumber2: e.target.value.replace(/\D/g, '').slice(0, 18) })}
        />
        {showErrors && errors.accountNumber2 && (
          <div className="invalid-feedback d-block">{errors.accountNumber2}</div>
        )}
      </div>

      <div className="mb-3">
        <label className="form-label">Account name</label>
        <input type="text" className="form-control" value={block.accountName} readOnly placeholder="Checked automatically" />
        {block.lookup === 'CHECKING' && (
          <div className="nova-field-note">
            <span className="spinner-border spinner-border-sm" aria-hidden="true" /> Checking the account…
          </div>
        )}
        {block.lookup === 'FOUND' && (
          <div className="nova-field-ok">
            <FontAwesomeIcon icon={faCircleCheck} /> Name as per bank records
          </div>
        )}
        {showErrors && errors.lookup && <div className="invalid-feedback d-block">{errors.lookup}</div>}
        {block.lookup === 'NOT_FOUND' && !showErrors && (
          <div className="invalid-feedback d-block">We couldn&apos;t find this account. Check the number and IFSC.</div>
        )}
      </div>

      <p className="nova-block-note">
        New beneficiaries can receive money 30 minutes after adding, up to ₹50,000 in the first 24 hours.
      </p>

      <PickerModal
        show={bankPickerOpen}
        title="Select bank"
        searchHint="Search bank"
        rows={banks.map((b) => ({ key: b.code, title: b.name, subtitle: b.ifscPrefix }))}
        emptyText="No matches"
        onSelect={(row) => {
          const bank = banks.find((b) => b.code === row.key);
          if (bank) pickBank(bank);
        }}
        onClose={() => setBankPickerOpen(false)}
      />
      <PickerModal
        show={branchPickerOpen}
        title="Select branch"
        searchHint="Search by branch, city or IFSC"
        rows={branches.map((b) => ({ key: b.ifsc, title: `${b.name}, ${b.city}`, subtitle: b.ifsc }))}
        emptyText="No matches"
        onSelect={(row) => {
          const branch = branches.find((b) => b.ifsc === row.key);
          if (branch) pickBranch(branch);
        }}
        onClose={() => setBranchPickerOpen(false)}
      />
    </div>
  );
};

export default LocalTransferFields;
